import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react'
import { Button, Dropdown, Form } from 'react-bootstrap'
import { useTranslation } from 'react-i18next'
import { correspondentService } from '../../services/correspondent.service'
import { customFieldService } from '../../services/custom-field.service'
import { documentTypeService } from '../../services/document-type.service'
import { documentService } from '../../services/document.service'
import { PermissionAction, permissionsService, PermissionType } from '../../services/permissions.service'
import { storagePathService } from '../../services/storage-path.service'
import { tagService } from '../../services/tag.service'
import { userService } from '../../services/user.service'
import { workflowStatusService } from '../../services/workflow-status.service'
import { ApiResponse, FilterRule, WorkflowStatus } from '../../types'
import { flattenTags } from '../../utils/flattenTags'
import CustomFieldsQueryDropdown, {
  CustomFieldQueriesModel,
  CustomFieldQueryElement,
  CustomFieldQueryElementType,
} from '../common/CustomFieldsQueryDropdown'
import DatesDropdown, { RELATIVE_DATE_QUERYSTRINGS, RelativeDate } from '../common/DatesDropdown'
import { DocumentationDialog } from '../common/DocumentationDialog'
import FilterableDropdown, { FilterableDropdownSelectionModel } from '../common/FilterableDropdown'
import PermissionsFilterDropdown, { OwnerFilterType, PermissionsSelectionModel } from '../common/PermissionsFilterDropdown'

interface FilterEditorProps {
  hidden?: boolean
  disabled?: boolean
  filterRules: FilterRule[]
  unmodifiedFilterRules: FilterRule[]
  selectionData: any
  onFilterRulesChange: (rules: FilterRule[]) => void
  onRequestClose?: () => void
  collapsed?: boolean
  onToggleCollapse?: () => void
}

export interface FilterEditorRef {
  toggleTag: (tagId: number) => void
  toggleCorrespondent: (correspondentId: number) => void
  toggleDocumentType: (documentTypeId: number) => void
  toggleWorkflowStatus: (workflowStatusId: number) => void
  toggleStoragePath: (storagePathId: number) => void
}

const TEXT_FILTER_TARGET_TITLE = 'title'
const TEXT_FILTER_TARGET_TITLE_CONTENT = 'title-content'
const TEXT_FILTER_TARGET_ASN = 'asn'
const TEXT_FILTER_TARGET_FULLTEXT_QUERY = 'fulltext-query'
const TEXT_FILTER_TARGET_FULLTEXT_MORELIKE = 'fulltext-morelike'
const TEXT_FILTER_TARGET_CUSTOM_FIELDS = 'custom-fields'
const TEXT_FILTER_TARGET_MIME_TYPE = 'mime-type'

const TEXT_FILTER_MODIFIER_EQUALS = 'equals'
const TEXT_FILTER_MODIFIER_NULL = 'is null'
const TEXT_FILTER_MODIFIER_NOTNULL = 'not null'
const TEXT_FILTER_MODIFIER_GT = 'greater'
const TEXT_FILTER_MODIFIER_LT = 'less'

const FILTER_TITLE = 0
const FILTER_TITLE_CONTENT = 19
const FILTER_ASN = 2
const FILTER_CORRESPONDENT = 3
const FILTER_DOCUMENT_TYPE = 4
const FILTER_HAS_TAGS_ALL = 6
const FILTER_HAS_TAGS_ANY = 22
const FILTER_HAS_ANY_TAG = 7
const FILTER_DOES_NOT_HAVE_TAG = 17
const FILTER_CREATED_FROM = 44
const FILTER_CREATED_TO = 43
const FILTER_ADDED_FROM = 46
const FILTER_ADDED_TO = 45
const FILTER_STORAGE_PATH = 25
const FILTER_CUSTOM_FIELDS_TEXT = 36
const FILTER_CUSTOM_FIELDS_QUERY = 42
const FILTER_FULLTEXT_QUERY = 20
const FILTER_FULLTEXT_MORELIKE = 21
const FILTER_MIME_TYPE = 47
const FILTER_ASN_ISNULL = 18
const FILTER_ASN_GT = 23
const FILTER_ASN_LT = 24
const FILTER_HAS_CORRESPONDENT_ANY = 26
const FILTER_DOES_NOT_HAVE_CORRESPONDENT = 27
const FILTER_HAS_DOCUMENT_TYPE_ANY = 28
const FILTER_DOES_NOT_HAVE_DOCUMENT_TYPE = 29
const FILTER_WORKFLOW_STATUS = 48
const FILTER_HAS_WORKFLOW_STATUS_ANY = 49
const FILTER_DOES_NOT_HAVE_WORKFLOW_STATUS = 50
const FILTER_HAS_STORAGE_PATH_ANY = 30
const FILTER_DOES_NOT_HAVE_STORAGE_PATH = 31
const FILTER_OWNER = 32
const FILTER_OWNER_ANY = 33
const FILTER_OWNER_ISNULL = 34
const FILTER_OWNER_DOES_NOT_INCLUDE = 35
const FILTER_SHARED_BY_USER = 37

const NEGATIVE_NULL_FILTER_VALUE = -1

// eslint-disable-next-line no-useless-escape
const RELATIVE_DATE_QUERY_REGEXP_CREATED = /created:[\["]([^\]]+)[\]"]/g
// eslint-disable-next-line no-useless-escape
const RELATIVE_DATE_QUERY_REGEXP_ADDED = /added:[\["]([^\]]+)[\]"]/g

const getTextFilterTargetOptions = (t: (key: string) => string) => [
  { id: TEXT_FILTER_TARGET_TITLE, name: t('title') },
  { id: TEXT_FILTER_TARGET_TITLE_CONTENT, name: t('title_and_content') },
  { id: TEXT_FILTER_TARGET_ASN, name: t('asn') },
  { id: TEXT_FILTER_TARGET_CUSTOM_FIELDS, name: t('custom_fields') },
  { id: TEXT_FILTER_TARGET_MIME_TYPE, name: t('file_type') },
  { id: TEXT_FILTER_TARGET_FULLTEXT_QUERY, name: t('advanced_search') },
]

// const getTextFilterTargetsWithMorelike = (t: (key: string) => string, hasMorelike: boolean) => {
//   const options = getTextFilterTargetOptions(t);
//   if (hasMorelike) {
//     const morelikeOption = getTextFilterTargetMorelikeOption(t);
//     const fulltextIndex = options.findIndex(o => o.id === TEXT_FILTER_TARGET_FULLTEXT_QUERY);
//     options.splice(fulltextIndex, 0, morelikeOption);
//   }
//   return options;
// };

const getTextFilterTargetMorelikeOption = (t: (key: string) => string) => ({
  id: TEXT_FILTER_TARGET_FULLTEXT_MORELIKE,
  name: t('more_like'),
})

const getTextFilterModifierOptions = (t: (key: string) => string) => [
  { id: TEXT_FILTER_MODIFIER_EQUALS, label: t('equals') },
  { id: TEXT_FILTER_MODIFIER_NULL, label: t('is_empty') },
  { id: TEXT_FILTER_MODIFIER_NOTNULL, label: t('is_not_empty') },
  { id: TEXT_FILTER_MODIFIER_GT, label: t('greater_than') },
  { id: TEXT_FILTER_MODIFIER_LT, label: t('less_than') },
]

enum LogicalOperator {
  And = 'and',
  Or = 'or',
}

enum Intersection {
  Include = 'include',
  Exclude = 'exclude',
}

enum ToggleableItemState {
  NotSelected = 0,
  Selected = 1,
  PartiallySelected = 2,
  Excluded = 3,
}

const FilterEditor = forwardRef<FilterEditorRef, FilterEditorProps>(
  (
    {
      hidden = false,
      disabled = false,
      filterRules,
      unmodifiedFilterRules,
      selectionData,
      onFilterRulesChange,
      onRequestClose,
      collapsed = false,
      onToggleCollapse,
    },
    ref
  ) => {
    const { t } = useTranslation()
    const [textFilter, setTextFilter] = useState('')
    const [textFilterTarget, setTextFilterTarget] = useState(TEXT_FILTER_TARGET_TITLE_CONTENT)
    const [textFilterModifier, setTextFilterModifier] = useState(TEXT_FILTER_MODIFIER_EQUALS)
    const [rulesModified, setRulesModified] = useState(false)
    const [show, setShow] = useState(false)
    const [moreLikeId, setMoreLikeId] = useState<number | null>(null)
    const [showFilterDocs, setShowFilterDocs] = useState(false)
    const filterChipClassName = collapsed ? 'filter-chip filter-chip--slim' : 'filter-chip w-100'
    const filterChipFullWidth = !collapsed

    const [tagSelectionModel, setTagSelectionModel] = useState(() => new FilterableDropdownSelectionModel(true, false)) // manyToOne = true for tags
    const [correspondentSelectionModel, setCorrespondentSelectionModel] = useState(() => new FilterableDropdownSelectionModel(false, false)) // manyToOne = false for others
    const [documentTypeSelectionModel, setDocumentTypeSelectionModel] = useState(() => new FilterableDropdownSelectionModel(false, false))
    const [workflowStatusSelectionModel, setWorkflowStatusSelectionModel] = useState(() => new FilterableDropdownSelectionModel(false, false))
    const [storagePathSelectionModel, setStoragePathSelectionModel] = useState(() => new FilterableDropdownSelectionModel(false, false))
    const [permissionsSelectionModel, setPermissionsSelectionModel] = useState<PermissionsSelectionModel>(() => ({
      ownerFilter: OwnerFilterType.NONE,
      hideUnowned: false,
      userID: null,
      includeUsers: [],
      excludeUsers: [],
    }))
    const [customFieldQueriesModel, setCustomFieldQueriesModel] = useState(() => new CustomFieldQueriesModel())
    const customFieldQueriesModelRef = useRef(customFieldQueriesModel)
    const customFieldQueryDebounceRef = useRef<NodeJS.Timeout | null>(null)
    const lastCustomFieldQueryRef = useRef<string>('')

    // Count filter rules for each type to display in badges
    const tagFilterCount = filterRules.filter((r) =>
      [FILTER_HAS_TAGS_ALL, FILTER_HAS_TAGS_ANY, FILTER_DOES_NOT_HAVE_TAG, FILTER_HAS_ANY_TAG].includes(r.rule_type as number)
    ).length

    const correspondentFilterCount = filterRules.filter((r) =>
      [FILTER_CORRESPONDENT, FILTER_HAS_CORRESPONDENT_ANY, FILTER_DOES_NOT_HAVE_CORRESPONDENT].includes(r.rule_type as number)
    ).length

    const documentTypeFilterCount = filterRules.filter((r) =>
      [FILTER_DOCUMENT_TYPE, FILTER_HAS_DOCUMENT_TYPE_ANY, FILTER_DOES_NOT_HAVE_DOCUMENT_TYPE].includes(r.rule_type as number)
    ).length

    const workflowStatusFilterCount = filterRules.filter((r) =>
      [FILTER_WORKFLOW_STATUS, FILTER_HAS_WORKFLOW_STATUS_ANY, FILTER_DOES_NOT_HAVE_WORKFLOW_STATUS].includes(r.rule_type as number)
    ).length

    const storagePathFilterCount = filterRules.filter((r) =>
      [FILTER_STORAGE_PATH, FILTER_HAS_STORAGE_PATH_ANY, FILTER_DOES_NOT_HAVE_STORAGE_PATH].includes(r.rule_type as number)
    ).length

    const [tagDocumentCounts, setTagDocumentCounts] = useState<{ [key: number]: number }>({})
    const [correspondentDocumentCounts, setCorrespondentDocumentCounts] = useState<{ [key: number]: number }>({})
    const [documentTypeDocumentCounts, setDocumentTypeDocumentCounts] = useState<{ [key: number]: number }>({})
    const [workflowStatusDocumentCounts, setWorkflowStatusDocumentCounts] = useState<{ [key: number]: number }>({})
    const [storagePathDocumentCounts, setStoragePathDocumentCounts] = useState<{ [key: number]: number }>({})
    const [users, setUsers] = useState<any[]>([])

    useEffect(() => {
      if (selectionData?.selected_tags) {
        const counts: { [key: number]: number } = {}
        selectionData.selected_tags.forEach((item: any) => {
          if (item.id && item.document_count !== undefined) {
            counts[item.id] = item.document_count
          }
        })
        setTagDocumentCounts(counts)
      }

      if (selectionData?.selected_correspondents) {
        const counts: { [key: number]: number } = {}
        selectionData.selected_correspondents.forEach((item: any) => {
          if (item.id && item.document_count !== undefined) {
            counts[item.id] = item.document_count
          }
        })
        setCorrespondentDocumentCounts(counts)
      }

      if (selectionData?.selected_document_types) {
        const counts: { [key: number]: number } = {}
        selectionData.selected_document_types.forEach((item: any) => {
          if (item.id && item.document_count !== undefined) {
            counts[item.id] = item.document_count
          }
        })
        setDocumentTypeDocumentCounts(counts)
      }

      if (selectionData?.selected_workflow_statuses) {
        const counts: { [key: number]: number } = {}
        selectionData.selected_workflow_statuses.forEach((item: any) => {
          if (item.id && item.document_count !== undefined) {
            counts[item.id] = item.document_count
          }
        })
        setWorkflowStatusDocumentCounts(counts)
      }

      if (selectionData?.selected_storage_paths) {
        const counts: { [key: number]: number } = {}
        selectionData.selected_storage_paths.forEach((item: any) => {
          if (item.id && item.document_count !== undefined) {
            counts[item.id] = item.document_count
          }
        })
        setStoragePathDocumentCounts(counts)
      }
    }, [selectionData])

    const [dateCreatedFrom, setDateCreatedFrom] = useState<string>('')
    const [dateCreatedTo, setDateCreatedTo] = useState<string>('')
    const [dateAddedFrom, setDateAddedFrom] = useState<string>('')
    const [dateAddedTo, setDateAddedTo] = useState<string>('')
    const [dateCreatedRelativeDate, setDateCreatedRelativeDate] = useState<RelativeDate | undefined>()
    const [dateAddedRelativeDate, setDateAddedRelativeDate] = useState<RelativeDate | undefined>()

    const textFilterInputRef = useRef<HTMLInputElement>(null)
    const debounceTimerRef = useRef<NodeJS.Timeout | null>(null)

    const lastProcessedRulesRef = useRef<string>('')

    const selectionDataLoadedRef = useRef<boolean>(false)

    const filterRulesRef = useRef<FilterRule[]>(filterRules)

    useImperativeHandle(ref, () => ({
      toggleTag: (tagId: number) => {
        tagSelectionModel.toggle(tagId)
        setTimeout(() => {
          updateRules()
        }, 50)
      },
      toggleCorrespondent: (correspondentId: number) => {
        correspondentSelectionModel.toggle(correspondentId)
        setTimeout(() => {
          updateRules()
        }, 50)
      },
      toggleDocumentType: (documentTypeId: number) => {
        documentTypeSelectionModel.toggle(documentTypeId)
        setTimeout(() => {
          updateRules()
        }, 50)
      },
      toggleWorkflowStatus: (workflowStatusId: number) => {
        workflowStatusSelectionModel.toggle(workflowStatusId)
        setTimeout(() => {
          updateRules()
        }, 50)
      },
      toggleStoragePath: (storagePathId: number) => {
        storagePathSelectionModel.toggle(storagePathId)
        setTimeout(() => {
          updateRules()
        }, 50)
      },
    }))

    useEffect(() => {
      let loadingCount = 0
      let completedCount = 0

      const maybeCompleteLoading = () => {
        completedCount++
        if (completedCount >= loadingCount) {
          selectionDataLoadedRef.current = true
          setShow(true)
          lastProcessedRulesRef.current = ''
          setTimeout(() => {
            parseFilterRulesIntoModels(filterRulesRef.current)
          }, 0)
        }
      }

      if (!selectionDataLoadedRef.current) {
        if (permissionsService.currentUserCan(PermissionAction.View, PermissionType.Tag)) {
          loadingCount++
          tagService
            .listAll()
            .then((tags) => {
              tagSelectionModel.items = flattenTags(tags)
              maybeCompleteLoading()
            })
            .catch(() => {
              maybeCompleteLoading()
            })
        }

        if (permissionsService.currentUserCan(PermissionAction.View, PermissionType.Correspondent)) {
          loadingCount++
          correspondentService
            .listAll()
            .then((response) => {
              correspondentSelectionModel.items = response.results || []
              maybeCompleteLoading()
            })
            .catch(() => {
              maybeCompleteLoading()
            })
        }

        if (permissionsService.currentUserCan(PermissionAction.View, PermissionType.DocumentType)) {
          loadingCount++
          documentTypeService
            .listAll()
            .then((response) => {
              documentTypeSelectionModel.items = response.results || []
              maybeCompleteLoading()
            })
            .catch(() => {
              maybeCompleteLoading()
            })
        }

        if (permissionsService.currentUserCan(PermissionAction.View, PermissionType.WorkflowStatus)) {
          loadingCount++
          workflowStatusService
            .listAll()
            .then((response: ApiResponse<WorkflowStatus>) => {
              workflowStatusSelectionModel.items = response.results || []
              maybeCompleteLoading()
            })
            .catch(() => {
              maybeCompleteLoading()
            })
        }

        if (permissionsService.currentUserCan(PermissionAction.View, PermissionType.StoragePath)) {
          loadingCount++
          storagePathService
            .listAll()
            .then((response) => {
              storagePathSelectionModel.items = response.results || []
              maybeCompleteLoading()
            })
            .catch(() => {
              maybeCompleteLoading()
            })
        }

        if (permissionsService.currentUserCan(PermissionAction.View, PermissionType.CustomField)) {
          loadingCount++
          customFieldService
            .listAll()
            .then(() => {
              maybeCompleteLoading()
            })
            .catch(() => {
              maybeCompleteLoading()
            })
        }

        if (permissionsService.currentUserCan(PermissionAction.View, PermissionType.User)) {
          loadingCount++
          userService
            .listAll()
            .then((response) => {
              setUsers(response.results || [])
              maybeCompleteLoading()
            })
            .catch(() => {
              maybeCompleteLoading()
            })
        }

        if (loadingCount === 0) {
          selectionDataLoadedRef.current = true
          setShow(true)
        }
      }

      if (textFilterInputRef.current) {
        textFilterInputRef.current.focus()
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []) // Load once on mount

    // Ažuriraj ref kada se filterRules promene
    useEffect(() => {
      filterRulesRef.current = filterRules
    }, [filterRules])

    useEffect(() => {
      setRulesModified(JSON.stringify(filterRules) !== JSON.stringify(unmodifiedFilterRules))
    }, [filterRules, unmodifiedFilterRules])

    // Use ref to always get current selection model state without triggering re-renders
    const tagSelectionModelRef = useRef(tagSelectionModel)
    const correspondentSelectionModelRef = useRef(correspondentSelectionModel)
    const documentTypeSelectionModelRef = useRef(documentTypeSelectionModel)
    const workflowStatusSelectionModelRef = useRef(workflowStatusSelectionModel)
    const storagePathSelectionModelRef = useRef(storagePathSelectionModel)

    // Update refs when models change
    useEffect(() => {
      tagSelectionModelRef.current = tagSelectionModel
    }, [tagSelectionModel])

    useEffect(() => {
      correspondentSelectionModelRef.current = correspondentSelectionModel
    }, [correspondentSelectionModel])

    useEffect(() => {
      documentTypeSelectionModelRef.current = documentTypeSelectionModel
    }, [documentTypeSelectionModel])

    useEffect(() => {
      workflowStatusSelectionModelRef.current = workflowStatusSelectionModel
    }, [workflowStatusSelectionModel])

    useEffect(() => {
      storagePathSelectionModelRef.current = storagePathSelectionModel
    }, [storagePathSelectionModel])

    // Load tags, correspondents, etc into selection models

    // Funkcija za parsiranje filterRules u selection models
    const parseFilterRulesIntoModels = (rules: FilterRule[]) => {
      // Allow empty array for reset operations
      if (!rules || rules.length === 0) {
        // Empty rules - reset everything
        isSyncingFromPropsRef.current = true
        tagSelectionModel.clear(false)
        correspondentSelectionModel.clear(false)
        documentTypeSelectionModel.clear(false)
        workflowStatusSelectionModel.clear(false)
        storagePathSelectionModel.clear(false)
        setTextFilter('')
        setDateCreatedFrom('')
        setDateCreatedTo('')
        setDateAddedFrom('')
        setDateAddedTo('')
        setDateCreatedRelativeDate(undefined)
        setDateAddedRelativeDate(undefined)
        setTextFilterModifier(TEXT_FILTER_MODIFIER_EQUALS)
        setMoreLikeId(null)
        setTextFilterTarget(TEXT_FILTER_TARGET_TITLE_CONTENT) // Reset to default
        setPermissionsSelectionModel({
          ownerFilter: OwnerFilterType.NONE,
          hideUnowned: false,
          userID: null,
          includeUsers: [],
          excludeUsers: [],
        })
        const emptyCustomFieldQueriesModel = new CustomFieldQueriesModel()
        setCustomFieldQueriesModel(emptyCustomFieldQueriesModel)
        customFieldQueriesModelRef.current = emptyCustomFieldQueriesModel
        setTimeout(() => {
          isSyncingFromPropsRef.current = false
        }, 150)
        return
      }

      isSyncingFromPropsRef.current = true

      // Resetuj DIREKTNO selection models (ne preko ref-ova)
      tagSelectionModel.clear(false)
      correspondentSelectionModel.clear(false)
      documentTypeSelectionModel.clear(false)
      workflowStatusSelectionModel.clear(false)
      storagePathSelectionModel.clear(false)
      setTextFilter('')
      setDateCreatedFrom('')
      setDateCreatedTo('')
      setDateAddedFrom('')
      setDateAddedTo('')
      setDateCreatedRelativeDate(undefined)
      setDateAddedRelativeDate(undefined)
      setTextFilterModifier(TEXT_FILTER_MODIFIER_EQUALS)
      setMoreLikeId(null)
      // CRITICAL: Reset textFilterTarget to default when rules are empty or don't contain more_like
      if (!rules || rules.length === 0 || !rules.some((r) => r.rule_type === FILTER_FULLTEXT_MORELIKE)) {
        setTextFilterTarget(TEXT_FILTER_TARGET_TITLE_CONTENT)
      }
      setPermissionsSelectionModel({
        ownerFilter: OwnerFilterType.NONE,
        hideUnowned: false,
        userID: null,
        includeUsers: [],
        excludeUsers: [],
      })

      // Reset custom field queries model
      const newCustomFieldQueriesModel = new CustomFieldQueriesModel()
      setCustomFieldQueriesModel(newCustomFieldQueriesModel)
      customFieldQueriesModelRef.current = newCustomFieldQueriesModel

      rules.forEach((rule) => {
        switch (rule.rule_type) {
          case FILTER_TITLE:
            setTextFilter(String(rule.value))
            setTextFilterTarget(TEXT_FILTER_TARGET_TITLE)
            break
          case FILTER_TITLE_CONTENT:
            setTextFilter(String(rule.value))
            setTextFilterTarget(TEXT_FILTER_TARGET_TITLE_CONTENT)
            break
          case FILTER_ASN:
            setTextFilter(String(rule.value))
            setTextFilterTarget(TEXT_FILTER_TARGET_ASN)
            break
          case FILTER_CUSTOM_FIELDS_TEXT:
            setTextFilter(String(rule.value))
            setTextFilterTarget(TEXT_FILTER_TARGET_CUSTOM_FIELDS)
            break
          case FILTER_MIME_TYPE:
            setTextFilter(String(rule.value))
            setTextFilterTarget(TEXT_FILTER_TARGET_MIME_TYPE)
            break
          case FILTER_FULLTEXT_QUERY:
            // Parse relative dates from query string
            const queryValue = String(rule.value)
            let hasRelativeDates = false

            // Check for created relative date
            const createdMatch = queryValue.match(RELATIVE_DATE_QUERY_REGEXP_CREATED)
            if (createdMatch && createdMatch.length > 0) {
              hasRelativeDates = true
              // eslint-disable-next-line no-useless-escape
              const dateQuery = createdMatch[0].replace(/created:[\["]/, '').replace(/[\]"]/, '')
              const rd = RELATIVE_DATE_QUERYSTRINGS.find((qS) => qS.dateQuery === dateQuery)
              if (rd) {
                setDateCreatedRelativeDate({
                  id: rd.relativeDate,
                  name: '', // Name will be set from translation
                  date: 0, // Date will be calculated
                })
              }
            }

            // Check for added relative date
            const addedMatch = queryValue.match(RELATIVE_DATE_QUERY_REGEXP_ADDED)
            if (addedMatch && addedMatch.length > 0) {
              hasRelativeDates = true
              // eslint-disable-next-line no-useless-escape
              const dateQuery = addedMatch[0].replace(/added:[\["]/, '').replace(/[\]"]/, '')
              const rd = RELATIVE_DATE_QUERYSTRINGS.find((qS) => qS.dateQuery === dateQuery)
              if (rd) {
                setDateAddedRelativeDate({
                  id: rd.relativeDate,
                  name: '', // Name will be set from translation
                  date: 0, // Date will be calculated
                })
              }
            }

            // If no relative dates, treat as normal fulltext query
            if (!hasRelativeDates) {
              setTextFilter(queryValue)
              setTextFilterTarget(TEXT_FILTER_TARGET_FULLTEXT_QUERY)
            } else {
              // Extract non-date query parts
              const nonDateQuery = queryValue
                .replace(RELATIVE_DATE_QUERY_REGEXP_CREATED, '')
                .replace(RELATIVE_DATE_QUERY_REGEXP_ADDED, '')
                .split(',')
                .filter((part) => part.trim().length > 0)
                .join(',')

              if (nonDateQuery) {
                setTextFilter(nonDateQuery)
                setTextFilterTarget(TEXT_FILTER_TARGET_FULLTEXT_QUERY)
              }
            }
            break
          case FILTER_FULLTEXT_MORELIKE:
            const docId = parseInt(String(rule.value))
            setMoreLikeId(docId)
            setTextFilterTarget(TEXT_FILTER_TARGET_FULLTEXT_MORELIKE)
            // Load document to show its title
            documentService
              .getDocument(docId)
              .then((doc) => {
                setTextFilter(doc.title || '')
              })
              .catch((_err) => {
                setTextFilter(String(rule.value))
              })
            break
          case FILTER_CREATED_FROM:
            setDateCreatedFrom(String(rule.value))
            break
          case FILTER_CREATED_TO:
            setDateCreatedTo(String(rule.value))
            break
          case FILTER_ADDED_FROM:
            setDateAddedFrom(String(rule.value))
            break
          case FILTER_ADDED_TO:
            setDateAddedTo(String(rule.value))
            break
          case FILTER_HAS_TAGS_ALL:
            tagSelectionModel.logicalOperator = LogicalOperator.And
            tagSelectionModel.set(rule.value ? +rule.value : null, ToggleableItemState.Selected, false)
            break
          case FILTER_HAS_TAGS_ANY:
            tagSelectionModel.logicalOperator = LogicalOperator.Or
            tagSelectionModel.set(rule.value ? +rule.value : null, ToggleableItemState.Selected, false)
            break
          case FILTER_DOES_NOT_HAVE_TAG:
            tagSelectionModel.set(rule.value ? +rule.value : null, ToggleableItemState.Excluded, false)
            break
          case FILTER_CORRESPONDENT:
            // Handle NEGATIVE_NULL_FILTER_VALUE (same as React)
            correspondentSelectionModel.intersection =
              rule.value === NEGATIVE_NULL_FILTER_VALUE.toString() ? Intersection.Exclude : Intersection.Include
            correspondentSelectionModel.set(
              rule.value ? +rule.value : null,
              correspondentSelectionModel.intersection === Intersection.Include ? ToggleableItemState.Selected : ToggleableItemState.Excluded,
              false
            )
            break
          case FILTER_HAS_CORRESPONDENT_ANY:
            correspondentSelectionModel.intersection = Intersection.Include
            correspondentSelectionModel.set(rule.value ? +rule.value : null, ToggleableItemState.Selected, false)
            break
          case FILTER_DOES_NOT_HAVE_CORRESPONDENT:
            correspondentSelectionModel.intersection = Intersection.Exclude
            correspondentSelectionModel.set(rule.value ? +rule.value : null, ToggleableItemState.Excluded, false)
            break
          case FILTER_DOCUMENT_TYPE:
            // Handle NEGATIVE_NULL_FILTER_VALUE (same as React)
            documentTypeSelectionModel.intersection =
              rule.value === NEGATIVE_NULL_FILTER_VALUE.toString() ? Intersection.Exclude : Intersection.Include
            documentTypeSelectionModel.set(
              rule.value ? +rule.value : null,
              documentTypeSelectionModel.intersection === Intersection.Include ? ToggleableItemState.Selected : ToggleableItemState.Excluded,
              false
            )
            break
          case FILTER_HAS_DOCUMENT_TYPE_ANY:
            documentTypeSelectionModel.intersection = Intersection.Include
            documentTypeSelectionModel.set(rule.value ? +rule.value : null, ToggleableItemState.Selected, false)
            break
          case FILTER_DOES_NOT_HAVE_DOCUMENT_TYPE:
            documentTypeSelectionModel.intersection = Intersection.Exclude
            documentTypeSelectionModel.set(rule.value ? +rule.value : null, ToggleableItemState.Excluded, false)
            break
          case FILTER_WORKFLOW_STATUS:
            workflowStatusSelectionModel.intersection =
              rule.value === NEGATIVE_NULL_FILTER_VALUE.toString() ? Intersection.Exclude : Intersection.Include
            workflowStatusSelectionModel.set(
              rule.value ? +rule.value : null,
              workflowStatusSelectionModel.intersection === Intersection.Include ? ToggleableItemState.Selected : ToggleableItemState.Excluded,
              false
            )
            break
          case FILTER_HAS_WORKFLOW_STATUS_ANY:
            workflowStatusSelectionModel.intersection = Intersection.Include
            workflowStatusSelectionModel.set(rule.value ? +rule.value : null, ToggleableItemState.Selected, false)
            break
          case FILTER_DOES_NOT_HAVE_WORKFLOW_STATUS:
            workflowStatusSelectionModel.intersection = Intersection.Exclude
            workflowStatusSelectionModel.set(rule.value ? +rule.value : null, ToggleableItemState.Excluded, false)
            break
          case FILTER_STORAGE_PATH:
            // Handle NEGATIVE_NULL_FILTER_VALUE (same as React)
            storagePathSelectionModel.intersection =
              rule.value === NEGATIVE_NULL_FILTER_VALUE.toString() ? Intersection.Exclude : Intersection.Include
            storagePathSelectionModel.set(
              rule.value ? +rule.value : null,
              storagePathSelectionModel.intersection === Intersection.Include ? ToggleableItemState.Selected : ToggleableItemState.Excluded,
              false
            )
            break
          case FILTER_HAS_STORAGE_PATH_ANY:
            storagePathSelectionModel.intersection = Intersection.Include
            storagePathSelectionModel.set(rule.value ? +rule.value : null, ToggleableItemState.Selected, false)
            break
          case FILTER_DOES_NOT_HAVE_STORAGE_PATH:
            storagePathSelectionModel.intersection = Intersection.Exclude
            storagePathSelectionModel.set(rule.value ? +rule.value : null, ToggleableItemState.Excluded, false)
            break
          case FILTER_ASN_ISNULL:
            setTextFilterTarget(TEXT_FILTER_TARGET_ASN)
            setTextFilterModifier(rule.value === 'true' || rule.value === '1' ? TEXT_FILTER_MODIFIER_NULL : TEXT_FILTER_MODIFIER_NOTNULL)
            break
          case FILTER_ASN_GT:
            setTextFilterTarget(TEXT_FILTER_TARGET_ASN)
            setTextFilterModifier(TEXT_FILTER_MODIFIER_GT)
            setTextFilter(String(rule.value))
            break
          case FILTER_ASN_LT:
            setTextFilterTarget(TEXT_FILTER_TARGET_ASN)
            setTextFilterModifier(TEXT_FILTER_MODIFIER_LT)
            setTextFilter(String(rule.value))
            break
          case FILTER_OWNER:
            setPermissionsSelectionModel((prev) => ({
              ...prev,
              ownerFilter: OwnerFilterType.SELF,
              userID: rule.value ? +rule.value : null,
            }))
            break
          case FILTER_OWNER_ANY:
            setPermissionsSelectionModel((prev) => ({
              ...prev,
              ownerFilter: OwnerFilterType.OTHERS,
              includeUsers: [...(prev.includeUsers || []), rule.value ? +rule.value : 0],
            }))
            break
          case FILTER_OWNER_DOES_NOT_INCLUDE:
            setPermissionsSelectionModel((prev) => ({
              ...prev,
              ownerFilter: OwnerFilterType.NOT_SELF,
              excludeUsers: [...(prev.excludeUsers || []), rule.value ? +rule.value : 0],
            }))
            break
          case FILTER_SHARED_BY_USER:
            setPermissionsSelectionModel((prev) => ({
              ...prev,
              ownerFilter: OwnerFilterType.SHARED_BY_ME,
              userID: rule.value ? +rule.value : null,
            }))
            break
          case FILTER_OWNER_ISNULL:
            if (rule.value === 'true' || rule.value === '1') {
              setPermissionsSelectionModel((prev) => ({
                ...prev,
                ownerFilter: OwnerFilterType.UNOWNED,
              }))
            } else {
              setPermissionsSelectionModel((prev) => ({
                ...prev,
                hideUnowned: true,
              }))
            }
            break
          case FILTER_CUSTOM_FIELDS_QUERY:
            // Parse custom field query from JSON using same logic as TriggerEditDialog
            try {
              const queryString = String(rule.value)
              if (!queryString) break

              const parsed = JSON.parse(queryString)
              if (!Array.isArray(parsed) || parsed.length === 0) break

              const parseToCustomFieldElement = (item: any): CustomFieldQueryElement | null => {
                if (Array.isArray(item)) {
                  if (item.length === 3) {
                    return {
                      type: CustomFieldQueryElementType.Atom,
                      atom: {
                        field: item[0],
                        operator: item[1],
                        value: item[2],
                      },
                    }
                  } else if (item.length === 2) {
                    const [operator, elements] = item
                    if (Array.isArray(elements)) {
                      return {
                        type: CustomFieldQueryElementType.Expression,
                        expression: {
                          operator: operator,
                          elements: elements.map((el) => parseToCustomFieldElement(el)).filter((e): e is CustomFieldQueryElement => e !== null),
                        },
                      }
                    }
                  }
                }
                return null
              }

              let elements: CustomFieldQueryElement[] = []
              let logicalOperator: 'OR' | 'AND' | 'NOT' = 'OR'

              if (parsed.length === 2 && Array.isArray(parsed[1])) {
                logicalOperator = parsed[0] as 'OR' | 'AND' | 'NOT'
                elements = parsed[1].map((el: any) => parseToCustomFieldElement(el)).filter((e): e is CustomFieldQueryElement => e !== null)
              } else {
                elements = parsed.map((item: any) => parseToCustomFieldElement(item)).filter((e): e is CustomFieldQueryElement => e !== null)
              }

              if (elements.length > 0) {
                newCustomFieldQueriesModel.logicalOperator = logicalOperator
                newCustomFieldQueriesModel.queries = [
                  {
                    type: CustomFieldQueryElementType.Expression,
                    expression: {
                      operator: logicalOperator,
                      elements: elements,
                    },
                  },
                ]
              }

              // Update state and ref
              setCustomFieldQueriesModel(newCustomFieldQueriesModel)
              customFieldQueriesModelRef.current = newCustomFieldQueriesModel
            } catch (error) {}
            break
        }
      })

      tagSelectionModel.apply()
      correspondentSelectionModel.apply()
      documentTypeSelectionModel.apply()
      workflowStatusSelectionModel.apply()
      storagePathSelectionModel.apply()

      const newTagModel = new FilterableDropdownSelectionModel(true, false)
      newTagModel.items = tagSelectionModel.items
      newTagModel.logicalOperator = tagSelectionModel.logicalOperator
      newTagModel.intersection = tagSelectionModel.intersection
      // Kopiraj selektovane stavke iz starog modela
      tagSelectionModel.getSelectedItems().forEach((item) => {
        newTagModel.set(item.id || null, ToggleableItemState.Selected, false)
      })
      tagSelectionModel.getExcludedItems().forEach((item) => {
        newTagModel.set(item.id || null, ToggleableItemState.Excluded, false)
      })
      newTagModel.apply()

      const newCorrespondentModel = new FilterableDropdownSelectionModel(false, false)
      newCorrespondentModel.items = correspondentSelectionModel.items
      newCorrespondentModel.logicalOperator = correspondentSelectionModel.logicalOperator
      newCorrespondentModel.intersection = correspondentSelectionModel.intersection
      correspondentSelectionModel.getSelectedItems().forEach((item) => {
        newCorrespondentModel.set(item.id || null, ToggleableItemState.Selected, false)
      })
      correspondentSelectionModel.getExcludedItems().forEach((item) => {
        newCorrespondentModel.set(item.id || null, ToggleableItemState.Excluded, false)
      })
      newCorrespondentModel.apply()

      const newDocTypeModel = new FilterableDropdownSelectionModel(false, false)
      newDocTypeModel.items = documentTypeSelectionModel.items
      newDocTypeModel.logicalOperator = documentTypeSelectionModel.logicalOperator
      newDocTypeModel.intersection = documentTypeSelectionModel.intersection
      documentTypeSelectionModel.getSelectedItems().forEach((item) => {
        newDocTypeModel.set(item.id || null, ToggleableItemState.Selected, false)
      })
      documentTypeSelectionModel.getExcludedItems().forEach((item) => {
        newDocTypeModel.set(item.id || null, ToggleableItemState.Excluded, false)
      })
      newDocTypeModel.apply()

      const newWorkflowStatusModel = new FilterableDropdownSelectionModel(false, false)
      newWorkflowStatusModel.items = workflowStatusSelectionModel.items
      newWorkflowStatusModel.logicalOperator = workflowStatusSelectionModel.logicalOperator
      newWorkflowStatusModel.intersection = workflowStatusSelectionModel.intersection
      workflowStatusSelectionModel.getSelectedItems().forEach((item) => {
        newWorkflowStatusModel.set(item.id || null, ToggleableItemState.Selected, false)
      })
      workflowStatusSelectionModel.getExcludedItems().forEach((item) => {
        newWorkflowStatusModel.set(item.id || null, ToggleableItemState.Excluded, false)
      })
      newWorkflowStatusModel.apply()

      const newStoragePathModel = new FilterableDropdownSelectionModel(false, false)
      newStoragePathModel.items = storagePathSelectionModel.items
      newStoragePathModel.logicalOperator = storagePathSelectionModel.logicalOperator
      newStoragePathModel.intersection = storagePathSelectionModel.intersection
      storagePathSelectionModel.getSelectedItems().forEach((item) => {
        newStoragePathModel.set(item.id || null, ToggleableItemState.Selected, false)
      })
      storagePathSelectionModel.getExcludedItems().forEach((item) => {
        newStoragePathModel.set(item.id || null, ToggleableItemState.Excluded, false)
      })
      newStoragePathModel.apply()

      setTagSelectionModel(newTagModel)
      setCorrespondentSelectionModel(newCorrespondentModel)
      setDocumentTypeSelectionModel(newDocTypeModel)
      setWorkflowStatusSelectionModel(newWorkflowStatusModel)
      setStoragePathSelectionModel(newStoragePathModel)

      // Ažuriraj ref-ove da pokazuju na nove instance (za updateRules)
      tagSelectionModelRef.current = newTagModel
      correspondentSelectionModelRef.current = newCorrespondentModel
      documentTypeSelectionModelRef.current = newDocTypeModel
      workflowStatusSelectionModelRef.current = newWorkflowStatusModel
      storagePathSelectionModelRef.current = newStoragePathModel

      // Odmah posle završenog parsiranja - DOZVOLI user akcije (nakon malo delay-a)
      setTimeout(() => {
        isSyncingFromPropsRef.current = false
      }, 150)
    }

    // Parse filter rules into UI state
    useEffect(() => {
      if (!filterRules) return

      // Check if rules actually changed
      const currentRulesStr = JSON.stringify(filterRules)
      if (lastProcessedRulesRef.current === currentRulesStr) {
        return // Skip if rules haven't changed
      }
      lastProcessedRulesRef.current = currentRulesStr

      parseFilterRulesIntoModels(filterRules)
    }, [filterRules])

    // Don't use useCallback here - we need fresh values on every call
    // Accept optional overrideTextFilter to handle async state updates
    const updateRules = (overrideTextFilter?: string) => {
      if (isUpdatingRulesRef.current) {
        return
      }

      if (isSyncingFromPropsRef.current) {
        return
      }

      isUpdatingRulesRef.current = true

      setTimeout(() => {
        isUpdatingRulesRef.current = false
      }, 500)

      const newFilterRules: FilterRule[] = []

      // Use override value if provided, otherwise use state
      const currentTextFilter = overrideTextFilter !== undefined ? overrideTextFilter : textFilter

      // Text filter
      if (currentTextFilter && textFilterTarget === TEXT_FILTER_TARGET_TITLE_CONTENT) {
        newFilterRules.push({ rule_type: FILTER_TITLE_CONTENT, value: currentTextFilter })
      }
      if (currentTextFilter && textFilterTarget === TEXT_FILTER_TARGET_TITLE) {
        newFilterRules.push({ rule_type: FILTER_TITLE, value: currentTextFilter })
      }
      if (textFilterTarget === TEXT_FILTER_TARGET_ASN) {
        if (textFilterModifier === TEXT_FILTER_MODIFIER_EQUALS && currentTextFilter) {
          newFilterRules.push({ rule_type: FILTER_ASN, value: currentTextFilter })
        } else if ([TEXT_FILTER_MODIFIER_NULL, TEXT_FILTER_MODIFIER_NOTNULL].includes(textFilterModifier)) {
          newFilterRules.push({
            rule_type: FILTER_ASN_ISNULL,
            value: (textFilterModifier === TEXT_FILTER_MODIFIER_NULL).toString(),
          })
        } else if ([TEXT_FILTER_MODIFIER_GT, TEXT_FILTER_MODIFIER_LT].includes(textFilterModifier) && currentTextFilter) {
          newFilterRules.push({
            rule_type: textFilterModifier === TEXT_FILTER_MODIFIER_GT ? FILTER_ASN_GT : FILTER_ASN_LT,
            value: currentTextFilter,
          })
        }
      }
      if (currentTextFilter && textFilterTarget === TEXT_FILTER_TARGET_CUSTOM_FIELDS) {
        newFilterRules.push({ rule_type: FILTER_CUSTOM_FIELDS_TEXT, value: currentTextFilter })
      }
      if (currentTextFilter && textFilterTarget === TEXT_FILTER_TARGET_MIME_TYPE) {
        newFilterRules.push({ rule_type: FILTER_MIME_TYPE, value: currentTextFilter })
      }
      if (currentTextFilter && textFilterTarget === TEXT_FILTER_TARGET_FULLTEXT_QUERY) {
        newFilterRules.push({ rule_type: FILTER_FULLTEXT_QUERY, value: currentTextFilter })
      }
      if (textFilterTarget === TEXT_FILTER_TARGET_FULLTEXT_MORELIKE && moreLikeId !== null) {
        newFilterRules.push({ rule_type: FILTER_FULLTEXT_MORELIKE, value: moreLikeId.toString() })
      }

      // Tags - use ref to avoid dependency
      const currentTagModel = tagSelectionModelRef.current
      if (currentTagModel.isNoneSelected()) {
        newFilterRules.push({ rule_type: FILTER_HAS_ANY_TAG, value: 'false' })
      } else {
        const tagFilterType = currentTagModel.logicalOperator === LogicalOperator.And ? FILTER_HAS_TAGS_ALL : FILTER_HAS_TAGS_ANY

        currentTagModel
          .getSelectedItems()
          .filter((tag) => tag.id)
          .forEach((tag) => {
            newFilterRules.push({ rule_type: tagFilterType, value: tag.id?.toString() || '' })
          })

        currentTagModel
          .getExcludedItems()
          .filter((tag) => tag.id)
          .forEach((tag) => {
            newFilterRules.push({ rule_type: FILTER_DOES_NOT_HAVE_TAG, value: tag.id?.toString() || '' })
          })
      }

      // Correspondents - use ref to avoid dependency
      const currentCorrespondentModel = correspondentSelectionModelRef.current
      if (currentCorrespondentModel.isNoneSelected() && currentCorrespondentModel.intersection === Intersection.Include) {
        newFilterRules.push({ rule_type: FILTER_CORRESPONDENT, value: null as any })
      } else {
        // Handle NEGATIVE_NULL_FILTER_VALUE (same as React)
        if (currentCorrespondentModel.isNoneSelected() && currentCorrespondentModel.intersection === Intersection.Exclude) {
          newFilterRules.push({
            rule_type: FILTER_CORRESPONDENT,
            value: NEGATIVE_NULL_FILTER_VALUE.toString(),
          })
        }
        currentCorrespondentModel.getSelectedItems().forEach((correspondent) => {
          newFilterRules.push({ rule_type: FILTER_HAS_CORRESPONDENT_ANY, value: correspondent.id?.toString() || '' })
        })
        currentCorrespondentModel
          .getExcludedItems()
          .filter((c) => (c.id ?? 0) > 0)
          .forEach((correspondent) => {
            newFilterRules.push({ rule_type: FILTER_DOES_NOT_HAVE_CORRESPONDENT, value: correspondent.id?.toString() || '' })
          })
      }

      // Document types - use ref to avoid dependency
      const currentDocTypeModel = documentTypeSelectionModelRef.current
      if (currentDocTypeModel.isNoneSelected() && currentDocTypeModel.intersection === Intersection.Include) {
        newFilterRules.push({ rule_type: FILTER_DOCUMENT_TYPE, value: null as any })
      } else {
        // Handle NEGATIVE_NULL_FILTER_VALUE (same as React)
        if (currentDocTypeModel.isNoneSelected() && currentDocTypeModel.intersection === Intersection.Exclude) {
          newFilterRules.push({
            rule_type: FILTER_DOCUMENT_TYPE,
            value: NEGATIVE_NULL_FILTER_VALUE.toString(),
          })
        }
        currentDocTypeModel.getSelectedItems().forEach((documentType) => {
          newFilterRules.push({ rule_type: FILTER_HAS_DOCUMENT_TYPE_ANY, value: documentType.id?.toString() || '' })
        })
        currentDocTypeModel
          .getExcludedItems()
          .filter((dt) => (dt.id ?? 0) > 0)
          .forEach((documentType) => {
            newFilterRules.push({ rule_type: FILTER_DOES_NOT_HAVE_DOCUMENT_TYPE, value: documentType.id?.toString() || '' })
          })
      }

      // Workflow statuses - use ref to avoid dependency
      const currentWorkflowStatusModel = workflowStatusSelectionModelRef.current
      if (currentWorkflowStatusModel.isNoneSelected() && currentWorkflowStatusModel.intersection === Intersection.Include) {
        newFilterRules.push({ rule_type: FILTER_WORKFLOW_STATUS, value: null as any })
      } else {
        if (currentWorkflowStatusModel.isNoneSelected() && currentWorkflowStatusModel.intersection === Intersection.Exclude) {
          newFilterRules.push({
            rule_type: FILTER_WORKFLOW_STATUS,
            value: NEGATIVE_NULL_FILTER_VALUE.toString(),
          })
        }
        currentWorkflowStatusModel.getSelectedItems().forEach((ws) => {
          newFilterRules.push({ rule_type: FILTER_HAS_WORKFLOW_STATUS_ANY, value: ws.id?.toString() || '' })
        })
        currentWorkflowStatusModel
          .getExcludedItems()
          .filter((ws) => (ws.id ?? 0) > 0)
          .forEach((ws) => {
            newFilterRules.push({ rule_type: FILTER_DOES_NOT_HAVE_WORKFLOW_STATUS, value: ws.id?.toString() || '' })
          })
      }

      // Storage paths - use ref to avoid dependency
      const currentStoragePathModel = storagePathSelectionModelRef.current
      if (currentStoragePathModel.isNoneSelected() && currentStoragePathModel.intersection === Intersection.Include) {
        newFilterRules.push({ rule_type: FILTER_STORAGE_PATH, value: null as any })
      } else {
        // Handle NEGATIVE_NULL_FILTER_VALUE (same as React)
        if (currentStoragePathModel.isNoneSelected() && currentStoragePathModel.intersection === Intersection.Exclude) {
          newFilterRules.push({
            rule_type: FILTER_STORAGE_PATH,
            value: NEGATIVE_NULL_FILTER_VALUE.toString(),
          })
        }
        currentStoragePathModel.getSelectedItems().forEach((storagePath) => {
          newFilterRules.push({ rule_type: FILTER_HAS_STORAGE_PATH_ANY, value: storagePath.id?.toString() || '' })
        })
        currentStoragePathModel
          .getExcludedItems()
          .filter((sp) => (sp.id ?? 0) > 0)
          .forEach((storagePath) => {
            newFilterRules.push({ rule_type: FILTER_DOES_NOT_HAVE_STORAGE_PATH, value: storagePath.id?.toString() || '' })
          })
      }

      // Dates - Relative dates (using query parameter)
      if (dateCreatedRelativeDate || dateAddedRelativeDate) {
        let queryArgs: string[] = []
        let existingRule = newFilterRules.find((fr) => fr.rule_type === FILTER_FULLTEXT_QUERY)

        // If had a title / content search and added a relative date we need to carry it over
        if (
          !existingRule &&
          textFilter?.length > 0 &&
          (textFilterTarget === TEXT_FILTER_TARGET_TITLE_CONTENT || textFilterTarget === TEXT_FILTER_TARGET_TITLE)
        ) {
          existingRule = newFilterRules.find((fr) => fr.rule_type === FILTER_TITLE_CONTENT || fr.rule_type === FILTER_TITLE)
          if (existingRule) {
            existingRule.rule_type = FILTER_FULLTEXT_QUERY
          }
        }

        const existingRuleArgs = existingRule?.value ? String(existingRule.value).split(',') : []

        if (dateCreatedRelativeDate) {
          const rd = RELATIVE_DATE_QUERYSTRINGS.find((qS) => qS.relativeDate === dateCreatedRelativeDate.id)
          if (rd) {
            queryArgs.push(`created:${rd.isRange ? `[${rd.dateQuery}]` : `"${rd.dateQuery}"`}`)
            if (existingRule) {
              queryArgs = existingRuleArgs.filter((arg: string) => !arg.match(RELATIVE_DATE_QUERY_REGEXP_CREATED)).concat(queryArgs)
            }
          }
        }

        if (dateAddedRelativeDate) {
          const rd = RELATIVE_DATE_QUERYSTRINGS.find((qS) => qS.relativeDate === dateAddedRelativeDate.id)
          if (rd) {
            queryArgs.push(`added:${rd.isRange ? `[${rd.dateQuery}]` : `"${rd.dateQuery}"`}`)
            if (existingRule) {
              queryArgs = existingRuleArgs.filter((arg: string) => !arg.match(RELATIVE_DATE_QUERY_REGEXP_ADDED)).concat(queryArgs)
            }
          }
        }

        if (existingRule) {
          existingRule.value = queryArgs.join(',')
        } else {
          newFilterRules.push({ rule_type: FILTER_FULLTEXT_QUERY, value: queryArgs.join(',') })
        }
      }

      // Dates - Manual date ranges (using date__gte and date__lte parameters)
      if (dateCreatedTo) {
        newFilterRules.push({ rule_type: FILTER_CREATED_TO, value: dateCreatedTo })
      }
      if (dateCreatedFrom) {
        newFilterRules.push({ rule_type: FILTER_CREATED_FROM, value: dateCreatedFrom })
      }
      if (dateAddedTo) {
        newFilterRules.push({ rule_type: FILTER_ADDED_TO, value: dateAddedTo })
      }
      if (dateAddedFrom) {
        newFilterRules.push({ rule_type: FILTER_ADDED_FROM, value: dateAddedFrom })
      }

      // Permissions
      if (permissionsSelectionModel.ownerFilter === OwnerFilterType.SELF) {
        newFilterRules.push({
          rule_type: FILTER_OWNER,
          value: permissionsSelectionModel.userID?.toString() || '',
        })
      } else if (permissionsSelectionModel.ownerFilter === OwnerFilterType.NOT_SELF) {
        if (permissionsSelectionModel.excludeUsers && permissionsSelectionModel.excludeUsers.length > 0) {
          permissionsSelectionModel.excludeUsers.forEach((userId) => {
            newFilterRules.push({
              rule_type: FILTER_OWNER_DOES_NOT_INCLUDE,
              value: userId.toString(),
            })
          })
        }
      } else if (permissionsSelectionModel.ownerFilter === OwnerFilterType.OTHERS) {
        if (permissionsSelectionModel.includeUsers && permissionsSelectionModel.includeUsers.length > 0) {
          permissionsSelectionModel.includeUsers.forEach((userId) => {
            newFilterRules.push({
              rule_type: FILTER_OWNER_ANY,
              value: userId.toString(),
            })
          })
        }
      } else if (permissionsSelectionModel.ownerFilter === OwnerFilterType.SHARED_BY_ME) {
        newFilterRules.push({
          rule_type: FILTER_SHARED_BY_USER,
          value: permissionsSelectionModel.userID?.toString() || '',
        })
      } else if (permissionsSelectionModel.ownerFilter === OwnerFilterType.UNOWNED) {
        newFilterRules.push({
          rule_type: FILTER_OWNER_ISNULL,
          value: 'true',
        })
      }

      if (permissionsSelectionModel.hideUnowned) {
        newFilterRules.push({
          rule_type: FILTER_OWNER_ISNULL,
          value: 'false',
        })
      }

      // Custom field queries - use ref to get latest value
      const currentCustomFieldQueriesModel = customFieldQueriesModelRef.current
      if (currentCustomFieldQueriesModel && currentCustomFieldQueriesModel.queries.length > 0 && currentCustomFieldQueriesModel.isValid()) {
        const customFieldQuery = currentCustomFieldQueriesModel.serialize()
        if (customFieldQuery && customFieldQuery !== '') {
          newFilterRules.push({
            rule_type: FILTER_CUSTOM_FIELDS_QUERY,
            value: customFieldQuery,
          })
        }
      }

      const newRulesStr = JSON.stringify(newFilterRules)
      const currentRulesStr = JSON.stringify(filterRules)

      if (newRulesStr !== currentRulesStr) {
        onFilterRulesChange(newFilterRules)
      }
    }

    const advancedFilters = (
      <>
        <CustomFieldsQueryDropdown
          title={t('custom_fields')}
          icon="ui-radios"
          selectionModel={customFieldQueriesModel}
          onSelectionModelChange={(model) => {
            customFieldQueriesModelRef.current = model

            if (isSyncingFromPropsRef.current) {
              return
            }

            if (model.isEmpty() || model.isValid()) {
              const serializedQuery = model.serialize()

              if (customFieldQueryDebounceRef.current) {
                clearTimeout(customFieldQueryDebounceRef.current)
                customFieldQueryDebounceRef.current = null
              }

              if (serializedQuery !== lastCustomFieldQueryRef.current && serializedQuery !== '') {
                lastCustomFieldQueryRef.current = serializedQuery

                if (isUpdatingRulesRef.current) {
                  return
                }

                customFieldQueryDebounceRef.current = setTimeout(() => {
                  if (!isUpdatingRulesRef.current && !isSyncingFromPropsRef.current) {
                    updateRules()
                  }
                }, 300)
              }
            }
          }}
          disabled={disabled}
          className={filterChipClassName}
          fullWidth={!collapsed}
        />

        <DatesDropdown
          title={t('dates')}
          icon="calendar-event"
          createdDateTo={dateCreatedTo}
          createdDateFrom={dateCreatedFrom}
          addedDateTo={dateAddedTo}
          addedDateFrom={dateAddedFrom}
          createdRelativeDate={dateCreatedRelativeDate}
          addedRelativeDate={dateAddedRelativeDate}
          onDatesSet={() => {
            updateRules()
          }}
          onCreatedDateToChange={setDateCreatedTo}
          onCreatedDateFromChange={setDateCreatedFrom}
          onAddedDateToChange={setDateAddedTo}
          onAddedDateFromChange={setDateAddedFrom}
          onCreatedRelativeDateChange={setDateCreatedRelativeDate}
          onAddedRelativeDateChange={setDateAddedRelativeDate}
          disabled={disabled}
          className={filterChipClassName}
          fullWidth={!collapsed}
        />

        <PermissionsFilterDropdown
          title={t('permissions')}
          icon="person-fill-lock"
          selectionModel={permissionsSelectionModel}
          onSelectionModelChange={setPermissionsSelectionModel}
          users={users}
          disabled={disabled}
          className={filterChipClassName}
          fullWidth={!collapsed}
        />
      </>
    )

    // Track initialization to avoid calling updateRules on mount
    const datesInitializedRef = useRef(false)
    const permissionsInitializedRef = useRef(false)

    // Flag to prevent updateRules() during sync from props (URL parsing)
    const isSyncingFromPropsRef = useRef(false)
    const isUpdatingRulesRef = useRef(false)

    // Call updateRules when date filters change (but not on mount)
    useEffect(() => {
      if (!datesInitializedRef.current) {
        datesInitializedRef.current = true
        return // Skip on first render
      }

      // ✅ BLOKIRAJ updateRules ako selection data još nije učitan (sprečava gubljenje filtera)
      if (!selectionDataLoadedRef.current) {
        return
      }

      // Only call updateRules if at least one date is set
      if (dateCreatedRelativeDate || dateAddedRelativeDate || dateCreatedFrom || dateCreatedTo || dateAddedFrom || dateAddedTo) {
        updateRules()
      }
    }, [dateCreatedRelativeDate, dateAddedRelativeDate, dateCreatedFrom, dateCreatedTo, dateAddedFrom, dateAddedTo])

    // Call updateRules when permissions change (but not on mount)
    useEffect(() => {
      if (!permissionsInitializedRef.current) {
        permissionsInitializedRef.current = true
        return // Skip on first render
      }

      // ✅ BLOKIRAJ updateRules ako selection data još nije učitan (sprečava gubljenje filtera)
      if (!selectionDataLoadedRef.current) {
        return
      }

      updateRules()
    }, [permissionsSelectionModel])

    // NE TREBA ovaj useEffect - counter je samo za forsirano menjanje key-a dropdown-a

    const handleTextFilterChange = (value: string) => {
      setTextFilter(value)

      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current)
      }

      // Pass the value directly to avoid React async state update issues
      debounceTimerRef.current = setTimeout(() => {
        updateRules(value)
      }, 800)
    }

    const handleTextFilterKeyup = (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (event.key === 'Enter') {
        if (debounceTimerRef.current) {
          clearTimeout(debounceTimerRef.current)
        }
        updateRules()
      } else if (event.key === 'Escape') {
        if (textFilter?.length) {
          setTextFilter('')
          updateRules()
        } else {
          textFilterInputRef.current?.blur()
        }
      }
    }

    const resetSelected = () => {
      setTextFilterTarget(TEXT_FILTER_TARGET_TITLE_CONTENT)
      setTextFilter('')
      setMoreLikeId(null)
      tagSelectionModel.clear()
      correspondentSelectionModel.clear()
      documentTypeSelectionModel.clear()
      workflowStatusSelectionModel.clear()
      storagePathSelectionModel.clear()
      setPermissionsSelectionModel({
        ownerFilter: OwnerFilterType.NONE,
        hideUnowned: false,
        userID: null,
        includeUsers: [],
        excludeUsers: [],
      })
      setDateCreatedFrom('')
      setDateCreatedTo('')
      setDateAddedFrom('')
      setDateAddedTo('')
      setDateCreatedRelativeDate(undefined)
      setDateAddedRelativeDate(undefined)
      customFieldQueriesModel.clear()
      customFieldQueriesModelRef.current = customFieldQueriesModel
      onFilterRulesChange(unmodifiedFilterRules)
    }

    const textFilterTargets =
      textFilterTarget === TEXT_FILTER_TARGET_FULLTEXT_MORELIKE
        ? [...getTextFilterTargetOptions(t), getTextFilterTargetMorelikeOption(t)]
        : getTextFilterTargetOptions(t)

    const textFilterTargetName = textFilterTargets.find((t) => t.id === textFilterTarget)?.name || t('title_and_content')

    const textFilterModifierIsNull = [TEXT_FILTER_MODIFIER_NULL, TEXT_FILTER_MODIFIER_NOTNULL].includes(textFilterModifier)

    if (hidden) {
      return null
    }

    return (
      <div className={`filters-panel fade ${show ? 'show' : ''} ${collapsed ? 'filters-panel--collapsed' : ''}`}>
        <div className="filters-panel__header">
          <div className="d-flex align-items-center gap-2">
            <p className="filters-panel__title mb-0">{t('filters')}</p>
            {!collapsed && (
              <Button
                variant="outline-secondary"
                size="sm"
                className="p-0 d-flex align-items-center justify-content-center filters-panel__info"
                style={{ width: '24px', height: '24px' }}
                onClick={() => setShowFilterDocs(true)}
                title={t('help')}
              >
                <i className="bi bi-info-circle"></i>
              </Button>
            )}
          </div>
          <div className="filters-panel__header-actions">
            <Button
              variant="link"
              size="sm"
              type="button"
              className="filters-panel__reset"
              disabled={!rulesModified || disabled}
              onClick={resetSelected}
            >
              <i className="bi bi-arrow-counterclockwise me-1"></i>
              {t('reset_filters')}
            </Button>
            {onRequestClose && (
              <Button variant="outline-secondary" size="sm" type="button" className="filters-panel__close d-lg-none" onClick={onRequestClose}>
                <i className="bi bi-x-lg"></i>
              </Button>
            )}
            {onToggleCollapse && (
              <Button
                variant="outline-secondary"
                size="sm"
                type="button"
                className="filters-panel__collapse-toggle"
                onClick={onToggleCollapse}
                title={collapsed ? 'Proširi filtere' : 'Sažmi filtere'}
              >
                <i className={`bi ${collapsed ? 'bi-chevron-double-right' : 'bi-chevron-double-left'}`}></i>
              </Button>
            )}
          </div>
        </div>

        {!collapsed && (
          <div className="filters-panel__section">
            {/* <span className="filters-panel__label">{t('search')}</span> */}
            <div className="filters-panel__search-controls">
              <div className="filters-panel__search-row">
                <Dropdown className="filters-panel__scope w-100">
                  <Dropdown.Toggle variant="outline-secondary" size="sm" className="filters-panel__scope-toggle" disabled={disabled}>
                    {textFilterTargetName}
                  </Dropdown.Toggle>
                  <Dropdown.Menu className="shadow filters-panel__scope-menu w-100">
                    {textFilterTargets.map((target) => (
                      <Dropdown.Item
                        key={target.id}
                        active={textFilterTarget === target.id}
                        onClick={() => {
                          setTextFilterTarget(target.id)
                          textFilterInputRef.current?.focus()
                          updateRules()
                        }}
                      >
                        {target.name}
                      </Dropdown.Item>
                    ))}
                  </Dropdown.Menu>
                </Dropdown>
              </div>

              <div className="filters-panel__search-row filters-panel__search-row--input">
                {textFilterTarget === TEXT_FILTER_TARGET_ASN && (
                  <Form.Select
                    size="sm"
                    className="filters-panel__modifier"
                    value={textFilterModifier}
                    disabled={disabled}
                    onChange={(e) => {
                      setTextFilterModifier(e.target.value)
                      if (
                        textFilterModifierIsNull ||
                        ([TEXT_FILTER_MODIFIER_EQUALS, TEXT_FILTER_MODIFIER_GT, TEXT_FILTER_MODIFIER_LT].includes(e.target.value) && textFilter)
                      ) {
                        updateRules()
                      }
                    }}
                  >
                    {getTextFilterModifierOptions(t).map((m) => (
                      <option key={m.id} value={m.id}>
                        {m.label}
                      </option>
                    ))}
                  </Form.Select>
                )}

                <div className="filters-panel__input">
                  <Form.Control
                    ref={textFilterInputRef}
                    type="text"
                    size="sm"
                    disabled={textFilterModifierIsNull || disabled}
                    value={textFilter}
                    onChange={(e) => handleTextFilterChange(e.target.value)}
                    onKeyUp={handleTextFilterKeyup}
                    readOnly={textFilterTarget === TEXT_FILTER_TARGET_FULLTEXT_MORELIKE}
                    placeholder={t('search_documents')}
                  />
                  {textFilter && (
                    <Button
                      variant="link"
                      size="sm"
                      type="button"
                      className="filters-panel__clear"
                      aria-label={t('clear') || 'Clear'}
                      disabled={disabled}
                      onClick={() => {
                        setTextFilter('')
                        updateRules('')
                      }}
                    >
                      <i className="bi bi-x"></i>
                    </Button>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        <div className={`filters-panel__chips ${collapsed ? 'filters-panel__chips--collapsed' : ''}`}>
          {tagSelectionModel.items.length > 0 && (
            <FilterableDropdown
              title={t('tags')}
              icon="tag-fill"
              filterPlaceholder={t('filter_tags')}
              selectionModel={tagSelectionModel}
              documentCounts={tagDocumentCounts}
              externalSelectionCount={tagFilterCount}
              onSelectionModelChange={(model) => {
                setTagSelectionModel(model)
                tagSelectionModelRef.current = model

                if (isSyncingFromPropsRef.current) {
                  return
                }

                setTimeout(() => {
                  updateRules()
                }, 50)
              }}
              onOpened={() => {}}
              allowSelectNone={true}
              disabled={disabled}
              className={filterChipClassName}
              panel
              fullWidth={filterChipFullWidth}
            />
          )}

          {correspondentSelectionModel.items.length > 0 && (
            <FilterableDropdown
              title={t('correspondent')}
              icon="person-fill"
              filterPlaceholder={t('filter_correspondents')}
              selectionModel={correspondentSelectionModel}
              documentCounts={correspondentDocumentCounts}
              externalSelectionCount={correspondentFilterCount}
              onSelectionModelChange={(model) => {
                setCorrespondentSelectionModel(model)
                correspondentSelectionModelRef.current = model

                if (isSyncingFromPropsRef.current) {
                  return
                }

                setTimeout(() => {
                  updateRules()
                }, 50)
              }}
              onOpened={() => {}}
              allowSelectNone={true}
              disabled={disabled}
              className={filterChipClassName}
              panel
              fullWidth={filterChipFullWidth}
            />
          )}

          {documentTypeSelectionModel.items.length > 0 && (
            <FilterableDropdown
              title={t('document_type')}
              icon="file-earmark-fill"
              filterPlaceholder={t('filter_document_types')}
              selectionModel={documentTypeSelectionModel}
              documentCounts={documentTypeDocumentCounts}
              externalSelectionCount={documentTypeFilterCount}
              onSelectionModelChange={(model) => {
                setDocumentTypeSelectionModel(model)
                documentTypeSelectionModelRef.current = model

                if (isSyncingFromPropsRef.current) {
                  return
                }

                setTimeout(() => {
                  updateRules()
                }, 50)
              }}
              onOpened={() => {}}
              allowSelectNone={true}
              disabled={disabled}
              className={filterChipClassName}
              panel
              fullWidth={filterChipFullWidth}
            />
          )}

          {workflowStatusSelectionModel.items.length > 0 && (
            <FilterableDropdown
              title={t('document_workflow.status')}
              icon="flag-fill"
              filterPlaceholder={t('filter_workflow_statuses')}
              selectionModel={workflowStatusSelectionModel}
              documentCounts={workflowStatusDocumentCounts}
              externalSelectionCount={workflowStatusFilterCount}
              onSelectionModelChange={(model) => {
                setWorkflowStatusSelectionModel(model)
                workflowStatusSelectionModelRef.current = model

                if (isSyncingFromPropsRef.current) {
                  return
                }

                setTimeout(() => {
                  updateRules()
                }, 50)
              }}
              onOpened={() => {}}
              allowSelectNone={true}
              disabled={disabled}
              className={filterChipClassName}
              panel
              fullWidth={filterChipFullWidth}
            />
          )}

          {storagePathSelectionModel.items.length > 0 && (
            <FilterableDropdown
              title={t('storage_path')}
              icon="folder-fill"
              filterPlaceholder={t('filter_storage_paths')}
              selectionModel={storagePathSelectionModel}
              documentCounts={storagePathDocumentCounts}
              externalSelectionCount={storagePathFilterCount}
              onSelectionModelChange={(model) => {
                setStoragePathSelectionModel(model)
                storagePathSelectionModelRef.current = model

                if (isSyncingFromPropsRef.current) {
                  return
                }

                setTimeout(() => {
                  updateRules()
                }, 50)
              }}
              onOpened={() => {}}
              allowSelectNone={true}
              disabled={disabled}
              className={filterChipClassName}
              panel
              fullWidth={filterChipFullWidth}
            />
          )}

          {collapsed && advancedFilters}
        </div>

        {!collapsed && <div className="filters-panel__advanced">{advancedFilters}</div>}
        <DocumentationDialog show={showFilterDocs} onHide={() => setShowFilterDocs(false)} docKey="document_filters" />
      </div>
    )
  }
)

FilterEditor.displayName = 'FilterEditor'

export default FilterEditor
